import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
    TextField,
    Button,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    AlertTitle,
    Card,
    CardContent,
    Typography,
    Box,
    Grid,
    Paper,
    Divider,
    Chip,
    Breadcrumbs,
    Link,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useMutationCoordinate, useQueryCoordinate } from '../../clients/graphql/graphql.hooks';
import { jediAssetQueryBySymbol, jediUpsertAssetMutation } from '../../clients/graphql/pages/assets-queries';
import { useTheme } from '@mui/material/styles';

type Asset = {
    name: string;
    description: string;
    figi: string;
    pictureUrl?: string;
};

type Params = {
    symbol?: string;
};

const AssetLanding: React.FC = () => {
    const { symbol } = useParams<Params>();
    const [picture, setPicture] = useState<File | null>(null);
    const [pictureData, setPictureData] = useState<string | null>(null);
    const [pictureName, setPictureName] = useState<string | null>(null);
    const [marketCap, setMarketCap] = useState<string | null>(null);
    const [initialPictureUrl, setInitialPictureUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [success, setSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [assetData, setAssetData] = useState<Asset | null>(null);

    const theme = useTheme();
    const borderColor = theme.palette.mode === 'dark' ? 'gray' : 'white';

    const { data, loading: queryLoading, error: queryError } = useQueryCoordinate(jediAssetQueryBySymbol, {
        variables: { symbol: symbol?.toUpperCase() },
        skip: !symbol,
    });

    const [updateAsset] = useMutationCoordinate(jediUpsertAssetMutation, {});

    useEffect(() => {
        if (data?.assetSearch?.assets?.length > 0) {
            const asset = data.assetSearch.assets[0];
            setAssetData(asset);
            setInitialPictureUrl(asset.pictureUrl || null);
        } else if (queryError) {
            setError(queryError.message);
        }
    }, [data, queryError]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            setPictureData(result.split(',')[1]);
            setPictureName(file.name);
        };
        reader.onerror = () => setError('Error reading file');
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            const response = await updateAsset({
                variables: {
                    symbol: symbol?.toUpperCase(),
                    name: assetData?.name,
                    description: assetData?.description,
                    pictureData,
                    pictureName,
                    figi: assetData?.figi,
                    marketCap,
                },
            });

            const pictureUrl = response?.data?.jediUpsertAsset?.asset?.pictureUrl;
            setInitialPictureUrl(pictureUrl || null);
            setPicture(null);
            setPictureData(null);
            setPictureName(null);
            setMarketCap(null);
            setLoading(false);
            setSuccess(true);
        } catch (err: any) {
            setLoading(false);
            setError(err.message || 'An error occurred');
        }
    };

    const handleDialogClose = () => {
        setSuccess(false);
        setError('');
        setLoading(false);
    };

    const handleCancel = () => {
        setPicture(null);
        setPictureData(null);
        setPictureName(null);
    };

    const isChanged = () => picture !== null;

    if (queryLoading) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress size={60} />
          </Box>
        );
    }

    if (!assetData) {
        return (
          <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
              <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
                  <Link component={RouterLink} to="/assets" underline="none" color="inherit">
                      Assets
                  </Link>
                  <Typography color="text.primary">{symbol?.toUpperCase()}</Typography>
              </Breadcrumbs>

              <Box sx={{ mb: 4 }}>
                  <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                      Asset Details
                  </Typography>
                  <Chip
                    label={`Symbol: ${symbol?.toUpperCase()}`}
                    color="primary"
                    variant="outlined"
                    sx={{ fontSize: '1rem', py: 1 }}
                  />
              </Box>

              <Alert severity="info" sx={{ mt: 3 }}>
                  <AlertTitle>Asset Not Found</AlertTitle>
                  No asset found with symbol "{symbol?.toUpperCase()}". Please check the symbol or add a new asset.
              </Alert>
          </Box>
        );
    }

    return (
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
              <Link component={RouterLink} to="/assets" underline="none" color="inherit">
                  Assets
              </Link>
              <Typography color="text.primary">{symbol?.toUpperCase()}</Typography>
          </Breadcrumbs>

          <Box sx={{ mb: 4 }}>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
                  Asset Details
              </Typography>
              <Chip
                label={`Symbol: ${symbol?.toUpperCase()}`}
                color="primary"
                variant="outlined"
                sx={{ fontSize: '1rem', py: 1 }}
              />
          </Box>

          <Card sx={{ boxShadow: 3 }}>
              <CardContent sx={{ p: 4 }}>
                  <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3, fontWeight: 500 }}>
                      Asset Information
                  </Typography>

                  <form onSubmit={handleSubmit}>
                      <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: 'fit-content' }}>
                                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                                      Asset Information
                                  </Typography>

                                  <TextField
                                    label="Asset Name"
                                    value={assetData.name}
                                    fullWidth
                                    margin="normal"
                                    disabled
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    InputProps={{ readOnly: true }}
                                  />

                                  <TextField
                                    label="Description"
                                    value={assetData.description}
                                    fullWidth
                                    margin="normal"
                                    disabled
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={{ mb: 2 }}
                                    InputProps={{ readOnly: true }}
                                  />

                                  <TextField
                                    label="Figi"
                                    value={assetData.figi}
                                    fullWidth
                                    margin="normal"
                                    disabled
                                    variant="outlined"
                                    sx={{ mb: 2 }}
                                    InputProps={{ readOnly: true }}
                                  />
                              </Paper>
                          </Grid>

                          <Grid item xs={12} md={6}>
                              <Paper elevation={1} sx={{ p: 3, height: 'fit-content' }}>
                                  <Typography variant="h6" gutterBottom sx={{ mb: 2, fontWeight: 500 }}>
                                      Asset Image
                                  </Typography>

                                  <input
                                    accept="image/*"
                                    type="file"
                                    onChange={handleFileChange}
                                    style={{ display: 'none' }}
                                    id="raised-button-file"
                                  />
                                  <label htmlFor="raised-button-file">
                                      <Button
                                        variant="outlined"
                                        component="span"
                                        disabled={queryLoading}
                                        startIcon={<PhotoCameraIcon />}
                                        sx={{ mb: 2 }}
                                      >
                                          Update Image
                                      </Button>
                                  </label>

                                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                      {initialPictureUrl && (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                                Current Image
                                            </Typography>
                                            <img
                                              src={initialPictureUrl}
                                              alt="Current"
                                              style={{
                                                  width: '120px',
                                                  height: '120px',
                                                  borderRadius: '12px',
                                                  border: `2px solid ${borderColor}`,
                                                  objectFit: 'cover',
                                              }}
                                            />
                                        </Box>
                                      )}
                                      {pictureData && (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                                                New Image
                                            </Typography>
                                            <img
                                              src={`data:image/*;base64,${pictureData}`}
                                              alt="Selected"
                                              style={{
                                                  width: '120px',
                                                  height: '120px',
                                                  borderRadius: '12px',
                                                  border: '2px solid #1976d2',
                                                  objectFit: 'cover',
                                              }}
                                            />
                                        </Box>
                                      )}
                                  </Box>
                              </Paper>
                          </Grid>
                      </Grid>

                      <Alert severity="warning" sx={{ mt: 3, mb: 3 }}>
                          <AlertTitle>Image Requirements</AlertTitle>
                          Please ensure images are under 100KB and in a common format (JPEG, PNG, GIF) before submitting.
                      </Alert>

                      <Divider sx={{ my: 3 }} />

                      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                          <Button
                            variant="outlined"
                            onClick={handleCancel}
                            disabled={loading || queryLoading}
                            startIcon={<CancelIcon />}
                          >
                              Cancel
                          </Button>
                          <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={loading || queryLoading}
                            startIcon={loading ? <CircularProgress size="1rem" /> : <SaveIcon />}
                            sx={{ minWidth: 120 }}
                          >
                              {loading ? 'Updating...' : 'Update Image'}
                          </Button>
                      </Box>
                  </form>
              </CardContent>
          </Card>

          <Dialog open={success} onClose={handleDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="success" />
                  Success
              </DialogTitle>
              <DialogContent>
                  <Typography>
                      Asset image updated successfully! Your changes have been saved.
                  </Typography>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleDialogClose} color="primary" variant="contained">
                      OK
                  </Button>
              </DialogActions>
          </Dialog>

          <Dialog open={Boolean(error)} onClose={handleDialogClose} maxWidth="sm" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ErrorIcon color="error" />
                  Error
              </DialogTitle>
              <DialogContent>
                  <Typography color="error">{error}</Typography>
              </DialogContent>
              <DialogActions>
                  <Button onClick={handleDialogClose} color="primary" variant="contained">
                      Close
                  </Button>
              </DialogActions>
          </Dialog>
      </Box>
    );
};

export default AssetLanding;
